import { computedValue, encounterCreatureField, inventoryField, itemsCol, layerEntriesOf, layerHas, layerOf, scalarLayerSlot } from '../../shared/array-types';
import { inventoryLineCostGp } from '../compile/inventory-totals';
import { inventoryDisplayReaders } from './inventory-totals-21';

type Row = Record<string, unknown>;
type Names = Record<string, { item_name: string; item_type?: string }>;
type LayerReader = (row: Row, key: string) => unknown;
export const ITEM_CARD_DEFINITION_NOT_HELD = 'itemcard31:definition-not-held';
export const ITEM_CARD_REQUIREMENTS = ['requires_ancestry', 'requires_lineage', 'requires_feat', 'requires_class', 'requires_subclass'] as const;

/** References can be UUIDs, shells or full held definitions; never stringify a shell. */
export function itemCardReferenceId(row: Row, layer: string): string {
    return scalarLayerSlot(layerOf(row, layer));
}

/** Availability is decided from the same held actor/row as the native action bridge. */
export function itemCardCanDisenchant(row: Row, actor: Row | null): boolean {
    const id = String(encounterCreatureField(actor?.encounter_creatures, 'encounter_creature_id') ?? '');
    return id !== '' && String(inventoryField(row, 'possess_id') ?? '').toUpperCase() === id.toUpperCase()
        && Number(layerOf(row, 'cost_gp') ?? 0) >= 100;
}

/** One small view of held data. No compile, fetch, mutation or encounter copy. */
export function itemCardView(row: Row, names: Names = {}, readLayer: LayerReader = layerOf, resolveName?: (id: string) => string) {
    const type = String(itemsCol(row, 'item_type') ?? '');
    const instance = inventoryField(row, 'inventory_id') != null;
    const lineGp = instance ? inventoryLineCostGp(row, inventoryDisplayReaders()) : null;
    const costGp = lineGp ?? computedValue(row, 'cost', 'total_gp')
        ?? readLayer(row, 'cost_gp') ?? readLayer(row, 'enchantment_total');
    const ownWeight = computedValue(row, 'weight', 'total') ?? readLayer(row, 'weight');
    const grossWeight = computedValue(row, 'weight', 'gross') ?? ownWeight;
    const capacity = readLayer(row, 'carrying_capacity_pounds');
    const requirements = ITEM_CARD_REQUIREMENTS.flatMap(layer => layerEntriesOf(row, layer).map(entry => {
        const id = scalarLayerSlot(entry);
        const resolved = itemsCol(entry, 'item_name') ?? names[id]?.item_name ?? names[id.toUpperCase()]?.item_name ?? resolveName?.(id);
        const name = resolved === id ? '' : resolved;
        return { layer, id, name: name || ITEM_CARD_DEFINITION_NOT_HELD,
            refusal: name ? null : ITEM_CARD_DEFINITION_NOT_HELD };
    }).filter(entry => entry.id !== ''));
    return {
        type, costGp, priceless: costGp != null && costGp !== '' && Number(costGp) === 0,
        ownWeight, grossWeight, capacity: capacity == null || capacity === '' ? null : Number(capacity),
        sizeId: String(inventoryField(row, 'inventory_size') || itemCardReferenceId(row, 'size')),
        level: (type === 'menta' ? layerOf(row, 'menta_level') : layerOf(row, 'spell_level')) ?? 0,
        levelIcon: itemCardReferenceId(row, type === 'menta' ? 'menta_mental_manifestation' : 'spell_circle'),
        repeatable: layerHas(row, 'feat_repeatable'), equipmentSlot: itemCardReferenceId(row, 'equipment_slot'),
        consumesUnitPerUse: layerHas(row, 'consumes_unit_per_use'),
        requiredLevel: layerOf(row, 'requires_level'), requiredClassLevel: layerOf(row, 'requires_class_level'),
        requirements,
    };
}
